import { forwardRef } from 'react';
import type { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';

import { cn } from '@utils/cn';

import { FormControl, FormDescription, FormItem, FormLabel, FormMessage } from './Form';

const Input = forwardRef<HTMLInputElement, InputProps>(({ className, type, ...props }, ref) => {
  return (
    <input
      className={cn(
        'flex h-12 w-full min-w-0 max-w-full rounded-md border border-border-input bg-light px-3 py-2',
        'font-primary text-md text-text-primary ring-offset-background',
        'file:border-0 file:bg-none file:text-sm file:font-medium placeholder:text-text-accent',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'shadow-md shadow-black/5 ring-1 ring-black/10 focus-visible:ring-primary/50',
        className,
      )}
      ref={ref}
      type={type}
      {...props}
    />
  );
});

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

Input.displayName = 'Input';

function InputForm<T extends FieldValues, K extends Path<T> = any>({
  className,
  errorMessage,
  field,
  helperText,
  label,
  required,
  placeholder,
  ...props
}: InputFormProps<T, K> & InputProps) {
  return (
    <FormItem className={className}>
      <FormLabel>
        {label} {required ? '*' : ''}
      </FormLabel>
      <FormControl>
        <Input
          {...field}
          {...props}
          placeholder={placeholder}
          required={required}
        />
      </FormControl>
      {helperText && <FormDescription>{helperText}</FormDescription>}
      {errorMessage && <FormMessage>{errorMessage}</FormMessage>}
    </FormItem>
  );
}

InputForm.displayName = 'InputForm';

type InputFormProps<T extends FieldValues, K extends Path<T>> = {
  className?: string;
  errorMessage?: string;
  field: ControllerRenderProps<T, K>;
  helperText?: string | React.ReactNode;
  label: string;
  placeholder?: string;
};

export { Input, InputForm };
